import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

export default function AdCarousel({ placement, position, className }) {
  const [ads, setAds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAds = async () => {
      try {
        const res = await api.get(`/api/ads/active?placement=${placement}&ad_type=carousel`);
        const filtered = res.data.filter(ad => ad.position === position);
        setAds(filtered);
      } catch (err) {
        console.error("Failed to load ads:", err);
      } finally {
        setLoading(false);
      }
    };

    loadAds();
  }, [placement, position]);

  useEffect(() => {
    if (ads.length === 0) return;

    // Track impression for current ad
    trackImpression(ads[currentIndex]?.ad_id);

    // Auto-rotate ads every 10 seconds
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % ads.length);
    }, 10000);

    return () => clearInterval(interval);
  }, [ads, currentIndex]);

  const trackImpression = async (adId) => {
    if (!adId) return;
    try {
      await api.post("/api/ads/impression", { ad_id: adId });
    } catch (err) {
      console.error("Failed to track impression:", err);
    }
  };

  const handleClick = async (ad) => {
    try {
      await api.post("/api/ads/click", { ad_id: ad.ad_id });
      if (ad.link_url) {
        window.open(ad.link_url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      console.error("Failed to track click:", err);
    }
  };

  const nextAd = () => {
    setCurrentIndex((prev) => (prev + 1) % ads.length);
  };

  const prevAd = () => {
    setCurrentIndex((prev) => (prev - 1 + ads.length) % ads.length);
  };

  if (loading || ads.length === 0) return null;

  const currentAd = ads[currentIndex];

  return (
    <div className={`relative group ${className}`}>
      {/* Ad Image */}
      <div
        onClick={() => handleClick(currentAd)}
        className="relative overflow-hidden rounded-xl cursor-pointer transition-transform hover:scale-[1.02]"
        style={{
          background: "linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)",
          border: "1px solid rgba(168, 85, 247, 0.2)"
        }}>
        <img
          src={currentAd.image_url}
          alt={currentAd.title}
          className="w-full h-full object-cover"
        />
        
        {/* Overlay Gradient */}
        <div 
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
          style={{
            background: "linear-gradient(0deg, rgba(0,0,0,0.7) 0%, transparent 50%)"
          }}
        />

        {/* Title on Hover */}
        <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <h4 className="text-white font-semibold text-sm flex items-center gap-2">
            {currentAd.title}
            {currentAd.link_url && <ExternalLink size={14} />}
          </h4>
        </div>

        {/* Ad Badge */}
        <div className="absolute top-2 left-2 px-2 py-1 bg-zinc-900/80 backdrop-blur-sm rounded text-xs text-zinc-400">
          Ad {currentIndex + 1}/{ads.length}
        </div>
      </div>

      {/* Navigation Arrows (only show if multiple ads) */}
      {ads.length > 1 && (
        <>
          <button
            onClick={prevAd}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 backdrop-blur-sm hover:bg-black/80 transition opacity-0 group-hover:opacity-100"
            style={{ color: "#a855f7" }}>
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={nextAd}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 backdrop-blur-sm hover:bg-black/80 transition opacity-0 group-hover:opacity-100"
            style={{ color: "#a855f7" }}>
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {ads.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-2">
          {ads.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition ${
                idx === currentIndex
                  ? "bg-purple-500 w-6"
                  : "bg-zinc-700 hover:bg-zinc-600"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
